import {
  CollectionReference,
  DocumentReference,
  firestore,
} from '../../../../functions/firebase/firebaseAccess';
import { WithID } from '../../../../functions/firebase/rxfireExtensions';
import {
  CurrentShowSpecialUrlCode,
  SpecialUrlCode,
} from '../../../../functions/urlCodeHelpers';
import {
  BlockedUser,
  Comment,
  LiveStream,
  Profile,
  ShippingMode,
  ShowCustomization,
  UpcomingShow,
} from '../../../../models/ClientModels';
import { assertNever } from '../../../../utils/typeHelper';
import { sortBy } from '../../../../utils/utils';
import { CurrentShow } from './useCurrentShow';

const defaultStoreReelBackground =
  'https://firebasestorage.googleapis.com/v0/b/popshoplive-26f81.appspot.com/o/appResources%2Fdefault-store-reel.jpg?alt=media';
export const defaultStoreReelVideo =
  'https://firebasestorage.googleapis.com/v0/b/popshoplive-26f81.appspot.com/o/appResources%2Fdefault-store-reel.mp4?alt=media';

const sellerInCurrentShow = (
  show: CurrentShow,
): WithID<Profile> | undefined => {
  switch (show.type) {
    case 'liveShow':
      return {
        ...show.liveStream.primary.profile,
        id: show.liveStream.primary.userId,
      };
    case 'replay':
      return {
        ...show.liveStream.primary.profile,
        id: show.liveStream.primary.userId,
      };
    case 'upcomingShow':
      return {
        ...show.upcomingShow.user,
        id: show.upcomingShow.userId,
      };
    case 'seller':
      return show.seller;
    case 'event':
      return undefined;
    case 'promotion':
      return undefined;
    default:
      assertNever(show);
  }
};

const avatarInCurrentShow = (show: CurrentShow): string | undefined => {
  switch (show.type) {
    case 'liveShow':
      return show.liveStream.primary.profile.avatar;
    case 'replay':
      return show.liveStream.primary.profile.avatar;
    case 'upcomingShow':
      return show.upcomingShow.user.avatar;
    case 'seller':
      return show.seller.avatar;
    case 'event':
      return show.event.backgroundURL ?? undefined;
    case 'promotion':
      return show.promotion.avatar;
    default:
      assertNever(show);
  }
};

const hostInCurrentShow = (
  show: CurrentShow,
): {
  id: string | undefined;
  avatar: string | undefined;
  name: string | undefined;
} => {
  const seller = sellerInCurrentShow(show);
  return {
    id: seller?.id,
    avatar: seller?.avatar,
    name: seller?.storeFrontName,
  };
};

const titleInCurrentShow = (show: CurrentShow) => {
  switch (show.type) {
    case 'event':
      return show.event.name;
    case 'upcomingShow':
      return show.upcomingShow.title;
    case 'liveShow':
      return show.liveStream.title;
    case 'replay':
      return show.liveStream.title;
    case 'promotion':
      return show.promotion.title;
    case 'seller':
      return show.store.name;
    default:
      assertNever(show);
  }
};

const verticalBackgroundInCurrentShow = (show: CurrentShow) => {
  switch (show.type) {
    case 'liveShow':
      return (
        show.liveStream.upcomingShow?.coverImage ?? show.liveStream.background
      );
    case 'replay':
      return (
        show.liveStream.upcomingShow?.coverImage ?? show.liveStream.background
      );
    case 'upcomingShow':
      return show.upcomingShow.coverImage;
    case 'event':
      return show.event.verticalBackgroundURL || show.event.backgroundURL;
    case 'promotion':
      return show.promotion.coverImage;
    case 'seller':
      return (
        show.store.defaultCoverImage ??
        show.store.evergreenVideo?.previewImageURL ??
        show.seller.avatar ??
        defaultStoreReelBackground
      );
    default:
      assertNever(show);
  }
};

const horizontalBackgroundInCurrentShow = (show: CurrentShow) => {
  switch (show.type) {
    case 'liveShow':
      return (
        show.liveStream.upcomingShow?.coverImage ?? show.liveStream.background
      );
    case 'replay':
      return (
        show.liveStream.upcomingShow?.coverImage ?? show.liveStream.background
      );
    case 'upcomingShow':
      return show.upcomingShow.coverImage;
    case 'event':
      return show.event.backgroundURL;
    case 'promotion':
      return show.promotion.coverImage;
    case 'seller':
      return (
        show.store.evergreenVideo?.previewImageURL ?? defaultStoreReelBackground
      );
    default:
      assertNever(show);
  }
};

const commentsPathForCurrentShow = (
  show: CurrentShow,
): CollectionReference<Comment> & {
  doc(id?: string): DocumentReference<Comment>;
} => {
  switch (show.type) {
    case 'liveShow':
      return firestore()
        .collection('liveStreams')
        .doc(show.liveStream.id)
        .collection('comments');
    case 'replay':
      return firestore()
        .collection('liveStreams')
        .doc(show.liveStream.id)
        .collection('comments');
    case 'upcomingShow':
      return firestore()
        .collection('upcomingShows')
        .doc(show.upcomingShow.id)
        .collection('comments');
    case 'event':
      return firestore()
        .collection('showEvents')
        .doc(show.event.id)
        .collection('comments');
    case 'promotion':
      return firestore()
        .collection('appGlobal')
        .doc(show.promotion.id)
        .collection('comments');
    case 'seller':
      return firestore()
        .collection('stores')
        .doc(show.store.id)
        .collection('comments');
    default:
      assertNever(show);
  }
};

const blockedUsersForCurrentShow = (show: CurrentShow) => {
  switch (show.type) {
    case 'liveShow':
      return firestore()
        .collection('liveStreams')
        .doc(show.liveStream.id)
        .collection('blocked');
    case 'replay':
      return firestore()
        .collection('liveStreams')
        .doc(show.liveStream.id)
        .collection('blocked');
    case 'upcomingShow':
      return firestore()
        .collection('upcomingShows')
        .doc(show.upcomingShow.id)
        .collection('blocked');
    case 'event':
      return firestore()
        .collection('showEvents')
        .doc(show.event.id)
        .collection('blocked');
    case 'promotion':
      return firestore()
        .collection('appGlobal')
        .doc(show.promotion.id)
        .collection('blocked');
    case 'seller':
      return firestore()
        .collection('stores')
        .doc(show.store.id)
        .collection('blocked');
    default:
      assertNever(show);
  }
};

const identifierInCurrentShow = (show: CurrentShow) => {
  switch (show.type) {
    case 'event':
      return show.event.id;
    case 'upcomingShow':
      return show.upcomingShow.id;
    case 'replay':
      return show.liveStream.id;
    case 'liveShow':
      return show.liveStream.id;
    case 'promotion':
      return show.promotion.id;
    case 'seller':
      return show.store.id;
    default:
      assertNever(show);
  }
};

const isCurrentShowActive = (show: CurrentShow) => {
  switch (show.type) {
    case 'event':
      return show.event.active;
    case 'upcomingShow':
      return show.upcomingShow.available;
    case 'liveShow':
      const { finished, aborted } = show.liveStream.primary.status;
      return !finished && !aborted;
    case 'replay':
      return true;
    case 'promotion':
      return show.promotion.enabled;
    case 'seller':
      return true;
    default:
      assertNever(show);
  }
};

const showRelevantDate = (show: CurrentShow) => {
  switch (show.type) {
    case 'upcomingShow':
      return show.upcomingShow.startsAt?.toDate();
    case 'liveShow':
      return show.liveStream.createdAt?.toDate();
    case 'promotion':
      return undefined;
    case 'event':
      return show.event.startedAfter?.toDate();
  }
};
const listingTitle = (show: CurrentShow) => {
  switch (show.type) {
    case 'event':
      return 'Tickets';
    case 'seller':
      return `${show.seller.storeFrontName!}'s Best Seller`;
    case 'upcomingShow':
      return `${show.upcomingShow.user.storeFrontName}'s Best Seller`;
    case 'liveShow':
      return (
        show.liveStream.primary.profile.storeFrontName ?? show.liveStream.title
      );
    case 'replay':
      return (
        show.liveStream.primary.profile.storeFrontName ?? show.liveStream.title
      );
    case 'promotion':
      return 'promotion';
    default:
      assertNever(show);
  }
};

const isEmptyDataWithID = <T>(data?: WithID<T>) => {
  // if a document does not exist, rxfire's dataDoc function returns an
  // empty object or an object w/ only the `id` property if specified.
  if (data == null) {
    return true;
  }
  const keys = Object.keys(data);
  return !keys.length || (keys.length === 1 && keys[0] === 'id');
};

const currentShowToSpecialUrlCode = (
  show: CurrentShow,
): CurrentShowSpecialUrlCode => {
  switch (show.type) {
    case 'event':
      return SpecialUrlCode.event(show.event.id);
    case 'liveShow':
      return SpecialUrlCode.liveShow(show.liveStream.id);
    case 'replay':
      return SpecialUrlCode.replay(show.liveStream.id);
    case 'upcomingShow':
      return SpecialUrlCode.upcomingShow(show.upcomingShow.id);
    case 'promotion':
      return SpecialUrlCode.promotion(show.promotion.id);
    case 'seller':
      return SpecialUrlCode.profile(show.seller.id);
    default:
      assertNever(show);
  }
};

const isCurrentShowNftGated = (show: CurrentShow) => {
  switch (show.type) {
    case 'event':
      return false;
    case 'upcomingShow':
      return false;
    case 'liveShow':
      return !!show.liveStream.isNftGated;
    case 'replay':
      return !!show.liveStream.isNftGated;
    case 'promotion':
      return false;
    case 'seller':
      return false;
    default:
      assertNever(show);
  }
};

const isCurrentShowPatreonGated = (show: CurrentShow) => {
  switch (show.type) {
    case 'event':
      return false;
    case 'upcomingShow':
      return false;
    case 'liveShow':
      return !!show.liveStream.isPatreonGated;
    case 'replay':
      return !!show.liveStream.isPatreonGated;
    case 'promotion':
      return false;
    case 'seller':
      return false;
    default:
      assertNever(show);
  }
};

const isCurrentShowExisted = (show: CurrentShow) => {
  switch (show.type) {
    case 'event':
      return !isEmptyDataWithID(show.event);
    case 'upcomingShow':
      return !isEmptyDataWithID(show.upcomingShow);
    case 'liveShow':
      return !isEmptyDataWithID(show.liveStream);
    case 'replay':
      return !isEmptyDataWithID(show.liveStream);
    case 'promotion':
      return !isEmptyDataWithID(show.promotion);
    case 'seller':
      return !isEmptyDataWithID(show.store);
    default:
      assertNever(show);
  }
};

function shippingMethodsForCurrentShow(show: CurrentShow) {
  const methods: ShippingMode[] =
    show.type === 'liveShow' ? show.liveStream.shippingMode ?? [] : [];
  const matched = [ShippingMode.Pickup, ShippingMode.Shipping].filter(x =>
    methods.includes(x),
  );
  return matched.length > 0 ? matched : [ShippingMode.Shipping];
}

export type ShowInfo = {
  exists: boolean;
  /** Get the active live stream id from the current show, only live stream and upcoming show with active live stream should be considered */
  activeLiveStreamId: string | undefined;
  /** Get the related live stream id from the current show, only live stream, replay and upcoming show with active live stream should be considered */
  relatedLiveStreamId: string | undefined;
  liveStream: WithID<LiveStream> | undefined;
  upcoming: WithID<UpcomingShow> | undefined;
  identifier: string;
  store: {
    id: string | undefined;
    avatar: string | undefined;
    name: string | undefined;
  };
  listingTitle: string;
  eventId: string | undefined;
  seller: WithID<Profile> | undefined;
  title: string;
  avatar: string | undefined;
  background: string | undefined;
  horizontalBackground: string | undefined;
  showComment: boolean;
  commentCollectionRef: CollectionReference<Comment> & {
    doc(id?: string): DocumentReference<Comment>;
  };
  blockedUserCollectionRef(uid: string): DocumentReference<BlockedUser>;
  urlCode: string;
  isActive: boolean;
  relevantDate: Date | undefined;
  shippingMethods: ShippingMode[];
  sourceLiveStream: WithID<LiveStream> | undefined;
  isNftGated: boolean;
  isPatreonGated: boolean;
  showCustomization: ShowCustomization | undefined;
};

export function showInfo(show: CurrentShow.HasSeller): ShowInfo & {
  seller: WithID<Profile>;
  store: {
    id: string;
    name: Profile['storeFrontName'];
    avatar: Profile['avatar'];
  };
};
export function showInfo(show: CurrentShow): ShowInfo;
export function showInfo(show: CurrentShow): ShowInfo {
  return {
    get exists() {
      return isCurrentShowExisted(show);
    },
    get liveStream() {
      return show.type === 'liveShow' || show.type === 'replay'
        ? show.liveStream
        : undefined;
    },
    get activeLiveStreamId() {
      return show.type === 'liveShow'
        ? show.liveStream.id
        : show.type === 'upcomingShow'
        ? show.upcomingShow.liveStreamId
        : undefined;
    },
    get relatedLiveStreamId() {
      return show.type === 'liveShow'
        ? show.liveStream.id
        : show.type === 'replay'
        ? show.liveStream.id
        : show.type === 'upcomingShow'
        ? show.upcomingShow.liveStreamId
        : undefined;
    },
    get upcoming() {
      if (show.type === 'upcomingShow') {
        return show.upcomingShow;
      }
      return undefined;
    },
    get identifier() {
      return identifierInCurrentShow(show);
    },
    get store() {
      return hostInCurrentShow(show);
    },
    get listingTitle() {
      return listingTitle(show);
    },
    get eventId() {
      if (show.type === 'liveShow') {
        return show.liveStream.eventId;
      }
      if (show.type === 'upcomingShow') {
        return show.upcomingShow.eventId;
      }
    },
    get seller() {
      return sellerInCurrentShow(show);
    },
    get title() {
      return titleInCurrentShow(show);
    },
    get avatar() {
      return avatarInCurrentShow(show);
    },
    get background() {
      return verticalBackgroundInCurrentShow(show);
    },
    get horizontalBackground() {
      return horizontalBackgroundInCurrentShow(show);
    },
    get showComment() {
      return show.type !== 'promotion' && show.type !== 'seller';
    },
    get commentCollectionRef() {
      return commentsPathForCurrentShow(show);
    },
    blockedUserCollectionRef(uid: string) {
      return blockedUsersForCurrentShow(show).doc(uid);
    },
    get urlCode() {
      return currentShowToSpecialUrlCode(show);
    },
    get isActive() {
      return isCurrentShowActive(show);
    },
    get relevantDate() {
      return showRelevantDate(show);
    },
    get shippingMethods() {
      return shippingMethodsForCurrentShow(show);
    },
    get sourceLiveStream() {
      return show.type === 'liveShow'
        ? show.liveStream
        : show.type === 'seller' && show.sourceShow?.type === 'liveShow'
        ? show.sourceShow.liveStream
        : undefined;
    },
    get isNftGated() {
      return isCurrentShowNftGated(show);
    },
    get isPatreonGated() {
      return isCurrentShowPatreonGated(show);
    },
    get showCustomization() {
      return show.type === 'liveShow' || show.type === 'replay'
        ? show.liveStream.showCustomization
        : show.type === 'upcomingShow'
        ? show.upcomingShow.showCustomization
        : show.type === 'event'
        ? show.event.showCustomization
        : undefined;
    },
  };
}

export function sortCurrentShowsByStartTime(
  shows: CurrentShow[],
): CurrentShow[] {
  return sortBy(
    show => showInfo(show).relevantDate?.getTime() ?? Infinity,
    shows,
  );
}

export function sortCurrentShowsByIsLive(shows: CurrentShow[]): CurrentShow[] {
  return sortBy(show => (isLive(show) ? 0 : 1), shows);

  function isLive(show: CurrentShow): boolean {
    // prettier-ignore
    return (
      show.type === 'liveShow' ? true :
      show.type === 'upcomingShow' ? show.upcomingShow.liveStreamId != null :
      false
    );
  }
}

type CurrentShowOfType<T, S = CurrentShow> = S extends { type: T } ? S : never;
type CurrentShowSelector<R, KT extends string = CurrentShow['type']> = {
  [K in KT]: (showRef: CurrentShowOfType<K>) => R;
};
export function currentShowSelect<T>(
  show: CurrentShow,
  selector: CurrentShowSelector<T>,
): T;
export function currentShowSelect<T, SRT extends string>(
  show: CurrentShowOfType<SRT>,
  selector: CurrentShowSelector<T, SRT>,
): T;
export function currentShowSelect<T>(
  show: CurrentShow,
  selector: Partial<CurrentShowSelector<T>>,
): undefined | T;
export function currentShowSelect<T>(
  show: CurrentShow,
  selector: Partial<CurrentShowSelector<T>>,
): undefined | T {
  return selector[show.type]?.(show as any);
}
